import { z } from "zod";
import { API_CONFIG_CATEGORIES, DIVULGACAO_MEDIA_TYPES, DIVULGACAO_STATUSES, INSTITUTION_STATUSES, ORIGENS_DA_GERACAO, PROMPT_TYPES, PURCHASE_TYPES, USER_ROLES, VIVENCIA_STATUSES, LANDING_EVENTOS, LANDING_INTERESSES, LANDING_TIPOS_EVENTO, } from "./enums.js";
/**
 * Todo schema de entrada vive aqui. No sistema anterior a validação era declarada
 * inline nos routers — 11 `z.object` só em `generations` — e as rotas REST não
 * validavam nada, usando `req.body as {...}`.
 */
// ─── Autenticação e usuários ──────────────────────────────────────────────────
export const loginSchema = z.object({
    email: z.email("Informe um e-mail válido").max(200),
    password: z.string().min(1, "Informe a senha").max(200),
});
export const pedirRedefinicaoSchema = z.object({
    email: z.email("Informe um e-mail válido").max(200),
});
export const redefinirSenhaSchema = z.object({
    codigo: z
        .string()
        .trim()
        .regex(/^\d{6}$/, "O código tem 6 dígitos"),
    novaSenha: z.string().min(8, "A senha precisa de ao menos 8 caracteres").max(200),
});
export const createUserSchema = z.object({
    name: z.string().min(2, "Informe o nome").max(200),
    email: z.email("Informe um e-mail válido").max(200),
    password: z.string().min(8, "A senha precisa de ao menos 8 caracteres").max(200),
    role: z.enum(USER_ROLES),
    /** Para operador/kiosk: o cliente a quem a conta pertence. */
    ownerUserId: z.number().int().positive().nullable().optional(),
});
export const updateUserSchema = z.object({
    id: z.number().int().positive(),
    name: z.string().min(2).max(200).optional(),
    email: z.email("Informe um e-mail válido").max(200).optional(),
    password: z.string().min(8).max(200).optional(),
    role: z.enum(USER_ROLES).optional(),
    ownerUserId: z.number().int().positive().nullable().optional(),
    /** Pode enviar pelo WhatsApp da SER (#105). Só vale para a conta de cliente: quem trabalha para ela herda. */
    whatsappLiberado: z.boolean().optional(),
});
// ─── Configuração ─────────────────────────────────────────────────────────────
export const upsertConfigSchema = z.object({
    key: z.string().min(1).max(120),
    value: z.string().max(20_000),
    category: z.enum(API_CONFIG_CATEGORIES),
    description: z.string().max(500).optional(),
    isSecret: z.boolean().optional(),
});
// ─── Utilidades de domínio ────────────────────────────────────────────────────
/**
 * Normaliza um telefone brasileiro para E.164.
 *
 * Aceita as formas que o operador digita na prática: com máscara, sem máscara,
 * com ou sem o 55, com ou sem o nono dígito.
 */
export function normalizeWhatsapp(input) {
    const digitos = input.replace(/\D/g, "");
    if (digitos.length === 0)
        return "";
    const semDdi = digitos.startsWith("55") && digitos.length > 11 ? digitos.slice(2) : digitos;
    // Celular sem o nono dígito: 10 dígitos com DDD.
    if (semDdi.length === 10 && semDdi[2] && Number(semDdi[2]) >= 6) {
        return `+55${semDdi.slice(0, 2)}9${semDdi.slice(2)}`;
    }
    return `+55${semDdi}`;
}
export const whatsappE164Schema = z
    .string()
    .transform(normalizeWhatsapp)
    .refine((v) => /^\+55\d{10,11}$/.test(v), "Informe um número brasileiro válido com DDD");
/**
 * Cadastro que a própria pessoa faz.
 *
 * O WhatsApp é obrigatório aqui e opcional no cadastro do admin: é por ele que
 * o código de confirmação sai, e é o contato pelo qual o cliente será atendido
 * no dia do evento.
 */
export const cadastroPublicoSchema = z.object({
    name: z.string().min(2, "Informe seu nome").max(200),
    email: z.email("Informe um e-mail válido").max(200),
    password: z.string().min(8, "A senha precisa de ao menos 8 caracteres").max(200),
    whatsapp: whatsappE164Schema,
});
export const confirmarCadastroSchema = z.object({
    codigo: z
        .string()
        .trim()
        .regex(/^\d{6}$/, "O código tem 6 dígitos"),
});
export const addressSchema = z.object({
    cep: z.string().max(9).optional(),
    logradouro: z.string().max(200).optional(),
    numero: z.string().max(20).optional(),
    complemento: z.string().max(120).optional(),
    bairro: z.string().max(120).optional(),
    cidade: z.string().max(120).optional(),
    uf: z.string().length(2).optional(),
});
// ─── Instituições ─────────────────────────────────────────────────────────────
export const createInstitutionSchema = z.object({
    name: z.string().min(2).max(200),
    /** Só o admin usa: de qual cliente é este local. Para os demais papéis o backend ignora. */
    ownerUserId: z.number().int().positive().optional(),
    cnpj: z.string().max(20).optional(),
    contactName: z.string().max(200).optional(),
    contactWhatsapp: z.string().max(30).optional(),
    contactEmail: z.email("E-mail inválido").max(200).optional().or(z.literal("")),
    status: z.enum(INSTITUTION_STATUSES).optional(),
    /** Enviar as fotos desta casa para um grupo, em vez do WhatsApp de cada um. */
    whatsappToGroup: z.boolean().optional(),
    whatsappGroupJid: z.string().max(120).nullable().optional(),
    address: addressSchema.optional(),
});
export const updateInstitutionSchema = createInstitutionSchema.partial().extend({
    id: z.string().min(1),
});
// ─── Vivências ────────────────────────────────────────────────────────────────
export const createVivenciaSchema = z.object({
    name: z.string().min(2).max(200),
    /** Só o admin usa: de qual cliente é esta vivência. Para os demais papéis o backend ignora. */
    ownerUserId: z.number().int().positive().optional(),
    institutionId: z.string().min(1, "Selecione o local"),
    status: z.enum(VIVENCIA_STATUSES).optional(),
    address: addressSchema.optional(),
});
export const updateVivenciaSchema = createVivenciaSchema.partial().extend({
    id: z.string().min(1),
});
// ─── Participantes ────────────────────────────────────────────────────────────
export const createParticipantSchema = z.object({
    firstName: z.string().min(1).max(120),
    lastName: z.string().min(1).max(120),
    whatsapp: whatsappE164Schema,
    email: z.email("E-mail inválido").max(200).optional().or(z.literal("")),
});
export const updateParticipantSchema = z.object({
    id: z.string().min(1),
    firstName: z.string().min(1).max(120).optional(),
    lastName: z.string().min(1).max(120).optional(),
    whatsapp: whatsappE164Schema.optional(),
    email: z.email().max(200).optional().or(z.literal("")),
});
export const addPurchaseSchema = z.object({
    participantId: z.string().min(1),
    purchaseType: z.enum(PURCHASE_TYPES),
    externalProductId: z.string().max(120).optional(),
    notes: z.string().max(1000).optional(),
});
export const registerParticipationSchema = z.object({
    vivenciaId: z.string().min(1),
    participantId: z.string().min(1),
});
// ─── Gerações ─────────────────────────────────────────────────────────────────
export const createGenerationSchema = z.object({
    spectatorName: z.string().min(1, "Informe o nome do espectador").max(200),
    spectatorPhotoPath: z.string().min(1, "Envie a foto do espectador"),
    participantId: z.string().min(1).optional(),
    vivenciaId: z.string().min(1).optional(),
    auxImagePaths: z.array(z.string().min(1)).max(4).optional(),
    imagePrompt: z.string().max(10_000).optional(),
});
export const updateGenerationSchema = z.object({
    id: z.number().int().positive(),
    spectatorName: z.string().min(1).max(200).optional(),
    imagePrompt: z.string().max(10_000).optional(),
    auxImagePaths: z.array(z.string().min(1)).max(4).optional(),
    participantId: z.string().min(1).nullable().optional(),
    vivenciaId: z.string().min(1).nullable().optional(),
});
/**
 * A foto que acabou de subir pelo `/api/generations/upload-photo`, e só ela: o prefixo e o nome aleatório que o
 * `gerarCaminho` dá (`<uuid><extensão do arquivo enviado>`, `.bin` quando o arquivo não tinha extensão).
 *
 * Um caminho qualquer seria copiado para a biblioteca de quem pede — inclusive o arquivo publicado de outra casa,
 * cujo nome é data, hora e nome da pessoa. O nome aleatório do upload não se adivinha.
 */
const caminhoDeFotoEnviada = z
    .string()
    .regex(/^generations\/photos\/[0-9a-f-]{36}\.[a-z0-9]{1,8}$/, "Envie a foto do espectador");
/**
 * O atendimento "Só fotografia" (#95): a foto sai como foi tirada, sem IA, publicada e pronta para imprimir. Sem
 * vivência, por decisão do usuário — por isso o campo nem existe aqui.
 */
export const salvarFotografiaSchema = z.object({
    spectatorName: z.string().trim().min(1, "Informe o nome do espectador").max(200),
    spectatorPhotoPath: caminhoDeFotoEnviada,
    /**
     * Obrigatório (#101): sem vivência, é o único caminho da foto até o Local, e sem ele a foto some de todo filtro
     * por Local.
     */
    institutionId: z.string().min(1, "Selecione o local").max(100),
    participantId: z.string().min(1).optional(),
    /** "Mostrar na galeria do salão". A foto sem IA começa fora: o operador a põe quando quiser. */
    naGaleria: z.boolean().default(false),
});
/** Tirar da galeria do salão, ou pôr de volta, sem despublicar (#96). */
export const mostrarNaGaleriaSchema = z.object({
    ids: z.array(z.number().int().positive()).min(1).max(200),
    mostrar: z.boolean(),
});
/**
 * A Biblioteca lista **capturas**, não imagens: um card por foto tirada, com as
 * imagens que nasceram dela.
 *
 * `institutionId` é o Local da foto: o da vivência, ou, na foto sem vivência, o
 * que ela guarda (#101). É filtro porque a pergunta que o operador faz é "o que
 * foi feito nesta casa", e uma pessoa participa de várias.
 */
export const listCapturasSchema = z.object({
    /** Nome do espectador, nome do participante ou telefone. */
    search: z.string().max(200).optional(),
    vivenciaId: z.string().min(1).optional(),
    institutionId: z.string().min(1).optional(),
    /** Com IA ou só fotografia (#99). Uma captura sem IA tem uma imagem só, então filtrar as linhas é filtrar os cards. */
    origem: z.enum(ORIGENS_DA_GERACAO).optional(),
    /** Só as fotos de um lote. O número dele também entra na busca textual. */
    loteId: z.string().min(1).optional(),
    approvedOnly: z.boolean().optional(),
    dateFrom: z.iso.date().optional(),
    dateTo: z.iso.date().optional(),
    limit: z.number().int().min(1).max(100).default(24),
    offset: z.number().int().min(0).default(0),
});
// ─── Bibliotecas ──────────────────────────────────────────────────────────────
export const updateMediaLabelSchema = z.object({
    id: z.number().int().positive(),
    label: z.string().max(200).nullable(),
});
export const deleteManySchema = z.object({
    ids: z.array(z.number().int().positive()).min(1).max(200),
});
export const createPromptSchema = z.object({
    label: z.string().min(1).max(200),
    content: z.string().min(1).max(10_000),
    genre: z.string().max(120).optional(),
    type: z.enum(PROMPT_TYPES).optional(),
});
export const updatePromptSchema = createPromptSchema.partial().extend({
    id: z.number().int().positive(),
});
// ─── Divulgações ──────────────────────────────────────────────────────────────
export const updateDivulgacaoSchema = z.object({
    id: z.number().int().positive(),
    title: z.string().max(200).nullable().optional(),
    durationSeconds: z.number().int().min(1).max(120).optional(),
    status: z.enum(DIVULGACAO_STATUSES).optional(),
    mediaType: z.enum(DIVULGACAO_MEDIA_TYPES).optional(),
});
export const setVivenciaDivulgacoesSchema = z.object({
    /** Nulo é a lista do modo "Todas as vivências". */
    vivenciaId: z.string().min(1).nullable(),
    divulgacaoIds: z.array(z.number().int().positive()).max(100),
});
// ─── Música da galeria ────────────────────────────────────────────────────────
export const renomearMusicaSchema = z.object({
    id: z.number().int().positive(),
    titulo: z.string().trim().min(1, "Dê um título à música").max(200),
});
export const criarPlaylistSchema = z.object({
    nome: z.string().trim().min(1, "Dê um nome à playlist").max(120),
});
export const renomearPlaylistSchema = criarPlaylistSchema.extend({
    id: z.number().int().positive(),
});
export const musicasDaPlaylistSchema = z.object({
    id: z.number().int().positive(),
    /** A ordem é a da lista. Repetida não entra: a mesma música duas vezes seria erro de clique. */
    musicaIds: z
        .array(z.number().int().positive())
        .max(200)
        .refine((ids) => new Set(ids).size === ids.length, "Música repetida na playlist"),
});
/** O que a TV pede para tocar: uma playlist, ou músicas escolhidas na hora. */
export const paraTocarSchema = z.union([
    z.object({ playlistId: z.number().int().positive() }),
    z.object({ musicaIds: z.array(z.number().int().positive()).min(1).max(200) }),
]);
// ─── Landing pública ──────────────────────────────────────────────────────────
const utmsLandingSchema = z
    .object({
    source: z.string().trim().max(200).optional(),
    medium: z.string().trim().max(200).optional(),
    campaign: z.string().trim().max(200).optional(),
    content: z.string().trim().max(200).optional(),
    term: z.string().trim().max(200).optional(),
})
    .optional();
export const landingInteresseSchema = z.object({
    nome: z.string().trim().min(2, "Informe seu nome").max(160),
    organizacao: z.string().trim().min(2, "Informe a organização ou evento").max(200),
    email: z.email("Informe um e-mail válido").max(200),
    whatsapp: whatsappE164Schema,
    tipoEvento: z.enum(LANDING_TIPOS_EVENTO),
    dataPrevista: z.iso.date("Informe uma data prevista"),
    participantesEstimados: z.number().int().min(1).max(1_000_000),
    cidadeEstado: z.string().trim().max(160).optional(),
    fotosEstimadas: z.number().int().min(1).max(1_000_000).optional(),
    interessePrincipal: z.enum(LANDING_INTERESSES).optional(),
    mensagem: z.string().trim().max(2_000).optional(),
    consentimentoContato: z.literal(true, { error: "Autorize o contato para enviar a solicitação" }),
    consentimentoMarketing: z.boolean().default(false),
    utms: utmsLandingSchema,
    /** Campo invisível: preenchido é robô, não pessoa. */
    empresaSite: z.string().max(200).optional(),
});
const propriedadeLanding = z.union([z.string().max(200), z.number(), z.boolean(), z.null()]);
export const landingEventoSchema = z.object({
    nome: z.enum(LANDING_EVENTOS),
    sessaoId: z.string().uuid(),
    pagina: z.string().trim().min(1).max(300),
    propriedades: z
        .record(z.string().max(50), propriedadeLanding)
        .refine((valor) => Object.keys(valor).length <= 12, "Muitas propriedades")
        .optional(),
});
//# sourceMappingURL=schemas.js.map